import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Divider,
  Paper,
  Tooltip,
  IconButton,
  Avatar
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { MuiColorInput } from 'mui-color-input';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import api from '../../services/api';
import ToastError from '../../toast/error/toastError';
import ToastSuccess from '../../toast/success/toastSuccess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import AlarmOnOutlinedIcon from '@mui/icons-material/AlarmOnOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useEvent } from '../../hooks/useEvent';

/**
 * Generates a random color in hexadecimal format with good contrast
 * @returns {string} Random color in hex format (#RRGGBB)
 */
const generateRandomColor = () => {
  // Generate more visually appealing colors with better saturation
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 30) + 70; // 70-100%
  const lightness = Math.floor(Math.random() * 20) + 40; // 40-60%

  // Convert HSL to HEX
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    g = Math.round(hue2rgb(p, q, h) * 255);
    b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  }

  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
};

/**
 * Enhanced Modal component for configuring Kanban column settings
 * @param {Object} props - Component props
 * @param {Object} props.kanban - Kanban board data
 * @param {string|number} props.columnId - ID of the column being edited
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onSave - Optional callback for after successful save
 */
const EditKanbanColumnSettingsModal = ({ kanban, columnId, open, onClose, onSave }) => {
  const emitter = useEvent();
  // Core state
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Archive modal state
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveAction, setArchiveAction] = useState('archive'); // 'archive' | 'move'
  const [moveToColumnId, setMoveToColumnId] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [otherColumns, setOtherColumns] = useState([]);

  // Data state
  const [column, setColumn] = useState(null);
  const [users, setUsers] = useState([]);
  const [connections, setConnections] = useState([]);

  // Form state
  const [color, setColor] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [formChanged, setFormChanged] = useState(false);

  // Derived state
  const showMessageTab = useMemo(() =>
    column?.sendMessage || false,
    [column?.sendMessage]
  );

  const greetingRef = useRef();

  // Reset state when modal closes
  const resetState = useCallback(() => {
    setColumn(null);
    setUsers([]);
    setConnections([]);
    setSelectedUser(null);
    setSelectedConnection(null);
    setActiveTab(0);
    setColor('');
    setFormChanged(false);
    setInitialLoadComplete(false);
    setArchiveModalOpen(false);
    setArchiveAction('archive');
    setMoveToColumnId('');
    setArchiveLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    // Use a timeout to prevent UI flicker
    setTimeout(resetState, 300);
  }, [onClose, resetState]);

  // Load data when modal opens
  useEffect(() => {
    if (!open || !columnId) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // Generate initial random color
        if (!color) {
          setColor(generateRandomColor());
        }

        // Parallel API requests for better performance
        const [columnResponse, usersResponse, connectionsResponse] = await Promise.all([
          api.get(`/kanban/column/${columnId}`),
          api.get('/users/', { params: { isActive: true, getAllUsers: true } }),
          api.get('/whatsapp/'),
        ]);

        const columnData = columnResponse.data;
        const activeUsers = usersResponse.data?.users || [];
        const availableConnections = connectionsResponse.data || [];

        // Set main data
        setConnections(availableConnections);
        setUsers(activeUsers);
        setColumn(columnData);

        // Set derived values
        if (columnData?.color) {
          setColor(columnData.color);
        }

        // Set selected user if exists
        if (columnData?.createTicketUser) {
          const user = activeUsers.find(u => u.id === columnData.createTicketUser);
          setSelectedUser(user || null);
        }

        // Set selected connection if exists
        if (columnData?.connectionId) {
          const connection = availableConnections.find(c => c.id === columnData.connectionId);
          setSelectedConnection(connection || null);
        }

        setInitialLoadComplete(true);
        setFormChanged(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        ToastError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, columnId]);

  // Track form changes
  useEffect(() => {
    if (!initialLoadComplete) return;
    setFormChanged(true);
  }, [
    column?.title, // Added title to the dependency array
    column?.hasStart,
    column?.hasFinished,
    column?.hasConcluded,
    column?.sendMessage,
    column?.message,
    column?.createTicket,
    column?.createTicketIn,
    selectedUser,
    selectedConnection,
    initialLoadComplete
  ]);

  useEffect(() => {
    if (column?.color == color) {
      setFormChanged(false)
    } else {
      setFormChanged(true)
    };
  }, [column?.color, color]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFormChange = (field, value) => {
    setColumn(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateColumnSettings = async () => {
    if (loading || !formChanged) return;

    try {
      setLoading(true);

      const payload = {
        title: column?.title || '', // Added title to the payload
        hasStart: column?.hasStart || false,
        hasFinished: column?.hasFinished || false,
        hasConcluded: column?.hasConcluded || false,
        sendMessage: column?.sendMessage || false,
        message: column?.message || '',
        createTicket: column?.createTicket || false,
        createTicketIn: column?.createTicketIn || 'pending',
        createTicketUser: selectedUser?.id || null,
        connectionId: selectedConnection?.id || null,
        color: color,
      };

      const { data } = await api.put(`/kanban/column/${columnId}`, payload);
      emitter.emit("kanban-column", {
        action: "update",
        kanbanId: kanban.id,
        columnId,
        column: data
      });
      // Notify success
      ToastSuccess('Configurações salvas com sucesso!');

      // Call the onSave callback if provided
      if (onSave && typeof onSave === 'function') {
        onSave(payload);
      }

      // Close modal on success
      handleClose();
    } catch (error) {
      console.error('Error updating column settings:', error);
      ToastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickAddText = (text) => {
    const currentMessage = column?.message || '';
    const newMessage = currentMessage + (currentMessage ? ' ' : '') + text;
    handleFormChange('message', newMessage);
  };

  const handleColorChange = (color) => {
    setColor(color);
  };

  const handleOpenArchiveModal = () => {
    // Build list of other columns from the kanban state
    if (kanban?.columnOrder) {
      const cols = kanban.columnOrder
        .map(cId => kanban.columns[cId])
        .filter(c => c && c.id !== `column-${columnId}`)
        .map(c => ({ id: c.id.replace('column-', ''), title: c.title }));
      setOtherColumns(cols);
    }
    setArchiveAction('archive');
    setMoveToColumnId('');
    setArchiveModalOpen(true);
  };

  const handleArchiveColumn = async () => {
    if (archiveAction === 'move' && !moveToColumnId) return;

    try {
      setArchiveLoading(true);
      const params = { action: 'archive' };
      if (archiveAction === 'move' && moveToColumnId) {
        params.moveToColumnId = moveToColumnId;
      }
      await api.delete(`/kanban/column/${columnId}`, { params });

      emitter.emit("kanban-column", {
        action: "delete",
        kanbanId: kanban?.id,
        columnId,
      });

      ToastSuccess('Coluna arquivada com sucesso!');
      setArchiveModalOpen(false);
      handleClose();
    } catch (error) {
      console.error('Error archiving column:', error);
      ToastError(error);
    } finally {
      setArchiveLoading(false);
    }
  };

  const renderActionTab = () => (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ColorLensOutlinedIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Aparência e Ações
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* New Title Input Field - First field in the form */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Título da coluna"
            name="title"
            value={column?.title || ''}
            onChange={(e) => handleFormChange('title', e.target.value)}
            variant="outlined"
            placeholder="Digite um título para a coluna"
            InputLabelProps={{ shrink: true }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <MuiColorInput
            format="hex"
            label="Cor da coluna"
            name="color"
            size="small"
            value={color}
            inputRef={greetingRef}
            onChange={handleColorChange}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }
            }}
          />
          <Box
            sx={{
              mt: 1,
              p: 1,
              height: 40,
              borderRadius: 1.5,
              bgcolor: color,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme => theme.palette.getContrastText(color || '#fff')
              }}
            >
              Prévia da cor selecionada
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AlarmOnOutlinedIcon color="info" sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Controle de Datas
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={column?.hasStart || false}
                onChange={(e) => handleFormChange('hasStart', e.target.checked)}
                color="primary"
              />
            }
            label="Registrar data/hora de início ao mover card para esta coluna"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={column?.hasFinished || false}
                onChange={(e) => handleFormChange('hasFinished', e.target.checked)}
                color="success"
              />
            }
            label="Registrar data/hora de conclusão ao mover card para esta coluna"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={column?.hasConcluded || false}
                onChange={(e) => handleFormChange('hasConcluded', e.target.checked)}
                color="primary"
              />
            }
            label="Registrar data/hora de finalização ao mover card para esta coluna"
          />

        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <MessageOutlinedIcon color="warning" sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Comunicação
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={column?.sendMessage || false}
                onChange={(e) => handleFormChange('sendMessage', e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span>Enviar mensagem automática ao solicitante</span>
                <Tooltip title="Quando ativado, uma mensagem será enviada automaticamente para o contato associado ao card">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const renderMessageTab = () => (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MessageOutlinedIcon color="warning" sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Configuração de Mensagem
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <Autocomplete
              value={selectedConnection}
              size="small"
              getOptionLabel={(option) => option.name || ''}
              onChange={(e, newValue) => setSelectedConnection(newValue)}
              options={connections}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: option?.status === "CONNECTED" ? 'success.main' : 'red',
                      mr: 1
                    }}
                  />
                  {option.name}
                </li>
              )}
              autoHighlight
              noOptionsText="Nenhuma conexão WhatsApp disponível"
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Conexão WhatsApp"
                  variant="outlined"
                  placeholder="Selecione uma conexão para envio"
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Mensagem a ser enviada"
            placeholder="Digite aqui a mensagem que será enviada automaticamente..."
            value={column?.message || ''}
            onChange={(e) => handleFormChange('message', e.target.value)}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
            InputProps={{
              sx: {
                fontFamily: 'monospace',
              }
            }}
          />

          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Adicione variáveis clicando nas opções abaixo:
          </Typography>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Chip
              size="small"
              variant="outlined"
              clickable
              label="Titulo do card"
              onClick={() => handleClickAddText('{{card_title}}')}
              color="primary"
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              variant="outlined"
              clickable
              label={`N° ${kanban?.taskTitle
                ? kanban.taskTitle.charAt(0).toUpperCase() + kanban.taskTitle.slice(1)
                : 'Protocolo'}`}
              onClick={() => handleClickAddText('{{card_protocol}}')}
              color="primary"
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              variant="outlined"
              clickable
              label="Nome do contato"
              onClick={() => handleClickAddText('{{contact_name}}')}
              color="primary"
              sx={{ borderRadius: 1 }}
            />

            {
              /*
              <Chip
                size="small"
                variant="outlined"
                clickable
                label="Data atual"
                onClick={() => handleClickAddText('{{current_date}}')}
                color="primary"
                sx={{ borderRadius: 1 }}
              />
              <Chip
                size="small"
                variant="outlined"
                clickable
                label="Título do card"
                onClick={() => handleClickAddText('{{card_title}}')}
                color="primary"
                sx={{ borderRadius: 1 }}
              />
              */
            }
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SupportAgentOutlinedIcon color="info" sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Opções de Atendimento
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={column?.createTicket || false}
                onChange={(e) => handleFormChange('createTicket', e.target.checked)}
                color="info"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span>Criar atendimento automático ao enviar mensagem</span>
                <Tooltip title="Quando ativado, um novo atendimento será criado para o contato">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            }
            sx={{ mb: 1 }}
          />

          {column?.createTicket && (
            <>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              >
                <InputLabel id="ticket-status-label">Status inicial do atendimento</InputLabel>
                <Select
                  labelId="ticket-status-label"
                  label="Status inicial do atendimento"
                  value={column?.createTicketIn || 'pending'}
                  onChange={(e) => handleFormChange('createTicketIn', e.target.value)}
                >
                  <MenuItem value="open">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main', mr: 1 }} />
                      Atendendo
                    </Box>
                  </MenuItem>
                  <MenuItem value="pending">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main', mr: 1 }} />
                      Pendente
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {column?.createTicketIn === 'open' && (
            <FormControl fullWidth size="small">
              <Autocomplete
                value={selectedUser}
                size="small"
                getOptionLabel={(option) => option.name || ''}
                onChange={(e, newValue) => setSelectedUser(newValue)}
                options={users}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Avatar src={option.driveUrl || option.media || ""} alt={option.name} sx={{ marginRight: '5px' }} />
                    {option.name}
                  </li>
                )}
                autoHighlight
                noOptionsText="Nenhum usuário disponível"
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Atribuir atendimento para"
                    variant="outlined"
                    placeholder="Selecione um usuário"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      }
                    }}
                  />
                )}
              />
            </FormControl>
          )}
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="kanban-settings-dialog"
      fullWidth
      maxWidth="md"
      BackdropProps={{
        style: {
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(0, 0, 30, 0.4)',
        },
      }}
    >
      <DialogTitle id="kanban-settings-dialog">
        {column?.title
          ? `Configurar Coluna: ${column.title}`
          : 'Configurações da Coluna'}
      </DialogTitle>

      <DialogContent dividers>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="kanban column settings tabs"
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 48,
              borderRadius: '8px 8px 0 0',
            },
            '& .Mui-selected': {
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            bgcolor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Tab
            label="Configurações Básicas"
            icon={<ColorLensOutlinedIcon />}
            iconPosition="start"
          />
          {showMessageTab && (
            <Tab
              label="Mensagem Automática"
              icon={<MessageOutlinedIcon />}
              iconPosition="start"
            />
          )}
        </Tabs>

        {loading && !initialLoadComplete ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {activeTab === 0 && renderActionTab()}
            {activeTab === 1 && renderMessageTab()}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="error"
          onClick={handleOpenArchiveModal}
          disabled={loading}
          sx={{ mr: 'auto' }}
        >
          Arquivar coluna
        </Button>
        <Button
          variant="contained"
          onClick={handleClose}
          color="error"
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdateColumnSettings}
          color="primary"
          disabled={loading || !formChanged}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Modal de confirmação de arquivamento */}
    <Dialog
      open={archiveModalOpen}
      onClose={() => !archiveLoading && setArchiveModalOpen(false)}
      aria-labelledby="archive-confirm-dialog"
      BackdropProps={{
        style: {
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(0, 0, 30, 0.4)',
        },
      }}
    >
      <DialogTitle id="archive-confirm-dialog">
        Arquivar coluna "{column?.title}"
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ whiteSpace: 'pre-line', mb: 2 }}>
          Ao arquivar esta coluna, ela será removida do quadro.{'\n'}Escolha o que fazer com os cards existentes:
        </Typography>

        <RadioGroup
          value={archiveAction}
          onChange={(e) => {
            setArchiveAction(e.target.value);
            if (e.target.value === 'archive') setMoveToColumnId('');
          }}
        >
          <FormControlLabel
            value="archive"
            control={<Radio />}
            label="Arquivar todos os cards junto com a coluna"
          />
          <FormControlLabel
            value="move"
            control={<Radio />}
            label="Mover cards para outra coluna antes de arquivar"
          />
        </RadioGroup>

        {archiveAction === 'move' && (
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Coluna de destino</InputLabel>
            <Select
              value={moveToColumnId}
              onChange={(e) => setMoveToColumnId(e.target.value)}
              label="Coluna de destino"
            >
              {otherColumns.map((col) => (
                <MenuItem key={col.id} value={col.id}>
                  {col.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setArchiveModalOpen(false)}
          color="error"
          disabled={archiveLoading}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleArchiveColumn}
          color="primary"
          disabled={archiveLoading || (archiveAction === 'move' && !moveToColumnId)}
        >
          {archiveLoading ? 'Arquivando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditKanbanColumnSettingsModal;